/* ==========================================================================
   Product Selector Component — Dual Mode (Flat Search + Cascading Browse)
   Issue #102: Both search methods visible — providers choose their path
   ========================================================================== */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { Combobox, type ComboboxOptionGroup } from '@/components/ui/Combobox'
import { Select, type SelectOption } from '@/components/ui/Select'
import { FormField } from '@/components/ui/FormField'
import { Loader2 } from 'lucide-react'
import { getCatalog } from '@/api/portal'
import { usePortal, usePortalSlug } from '@/context/PortalContext'
import { RecentProducts } from '@/components/forms/RecentProducts'
import type { CatalogProductForPortal } from '@/api/types'
import { compareCategories, UNCATEGORIZED_LABEL } from '@/utils/sort'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export interface ProductSelectorFormData {
  product_id: string
}

export interface ProductSelectorProps {
  /** Callback when a product is selected */
  onProductSelect?: (product: CatalogProductForPortal | null) => void
  /** Disable the selector */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

/* --------------------------------------------------------------------------
   Helper Functions
   -------------------------------------------------------------------------- */

/**
 * Parse a product's category field into an array of categories.
 * Categories come from the pharmacy-managed category list (PORTAL-77);
 * products without any become ["Uncategorized"].
 */
function getProductCategories(p: CatalogProductForPortal): string[] {
  return p.categories.length > 0 ? p.categories : ['Uncategorized']
}

/** Check if a product matches a category filter (null = all categories). */
function productMatchesCategory(
  p: CatalogProductForPortal,
  category: string | null
): boolean {
  if (!category) return true
  return getProductCategories(p).includes(category)
}

/**
 * Build a rich display label for a product variant.
 * Shows drug name + distinguishing details (strength, container size).
 */
function buildOptionLabel(p: CatalogProductForPortal): string {
  const parts = [p.display_name]
  if (p.strength) parts.push(p.strength)
  if (p.unit_amount) parts.push(p.unit_amount)
  return parts.join(' — ')
}

/**
 * Build secondary description text for a product option.
 * Shows product code as secondary info.
 */
function buildOptionDescription(p: CatalogProductForPortal): string | undefined {
  return p.product_code || undefined
}

/**
 * Group products by their primary category (first assigned category).
 */
function groupProductsByCategory(
  products: CatalogProductForPortal[]
): ComboboxOptionGroup[] {
  const grouped = new Map<string, { value: string; label: string; description?: string }[]>()

  products.forEach((p) => {
    const primaryCategory = getProductCategories(p)[0]

    const option = {
      value: p.id,
      label: buildOptionLabel(p),
      description: buildOptionDescription(p),
    }

    const existing = grouped.get(primaryCategory) || []
    grouped.set(primaryCategory, [...existing, option])
  })

  // Sort categories alphabetically, "Uncategorized" last
  const sortedCategories = Array.from(grouped.keys()).sort(compareCategories)

  return sortedCategories.map((category) => ({
    label: category,
    options: grouped.get(category) || [],
  }))
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function ProductSelector({
  onProductSelect,
  disabled = false,
  className = '',
}: ProductSelectorProps) {
  const slug = usePortalSlug()
  const { config } = usePortal()
  // PORTAL-80 (Richie's): client-specific product-selection customizations.
  const directionsMode = !!config?.directions_dropdown_enabled
  const hideCategoryBrowse = !!config?.hide_category_browse
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ProductSelectorFormData>()

  // --- Product fetch state ---
  const [products, setProducts] = useState<CatalogProductForPortal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // --- Shared selection state ---
  const [productSelected, setProductSelected] = useState(false)

  // --- Cascade browse state ---
  const [cascadeCategory, setCascadeCategory] = useState<string | null>(null)
  const [cascadeDrugName, setCascadeDrugName] = useState<string | null>(null)

  // --- Directions mode state (PORTAL-80): the drug picked in the flat list,
  // for which a "directions" dropdown of sibling products is then shown. ---
  const [selectedDrugName, setSelectedDrugName] = useState<string | null>(null)

  // Watch current product_id for pre-fill detection
  const currentProductId = watch('product_id')
  const prefillProcessed = useRef(false)

  // ─── Fetch catalog on mount ────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false

    async function fetchCatalog() {
      setLoading(true)
      setError(null)

      try {
        const catalogProducts = await getCatalog(slug)
        if (!cancelled) {
          setProducts(catalogProducts)
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load products. Please try again.')
          console.error('Error fetching catalog:', err)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchCatalog()
    return () => {
      cancelled = true
    }
  }, [slug])

  // ─── Flat search options (always show ALL products) ──────────────────

  const groupedOptions = useMemo(() => {
    if (products.length === 0) return []
    return groupProductsByCategory(products)
  }, [products])

  // ─── Cascade derived options ─────────────────────────────────────────

  /** Unique categories with product counts (for cascade step 1). */
  const cascadeCategoryOptions: SelectOption[] = useMemo(() => {
    const catCounts = new Map<string, number>()
    products.forEach((p) => {
      getProductCategories(p).forEach((cat) => {
        catCounts.set(cat, (catCounts.get(cat) || 0) + 1)
      })
    })
    return Array.from(catCounts.entries())
      .sort(([a], [b]) => compareCategories(a, b))
      .map(([cat, count]) => ({ value: cat, label: `${cat} (${count})` }))
  }, [products])

  /** Whether the cascade section should appear: at least one real category
   *  (PORTAL-151 — one is enough). An all-"Uncategorized" catalog stays hidden. */
  const showCascade = cascadeCategoryOptions.some(
    (o) => o.value !== UNCATEGORIZED_LABEL
  )

  /** Unique drug names within the selected cascade category. */
  const cascadeDrugOptions: SelectOption[] = useMemo(() => {
    if (!cascadeCategory) return []
    const drugMap = new Map<string, number>()
    products
      .filter((p) => productMatchesCategory(p, cascadeCategory))
      .forEach((p) => {
        drugMap.set(p.display_name, (drugMap.get(p.display_name) || 0) + 1)
      })
    return Array.from(drugMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({
        value: name,
        label: name,
        description: count > 1 ? `${count} options` : undefined,
      })) as SelectOption[]
  }, [products, cascadeCategory])

  /** Product variants matching cascade category + drug name (for step 3). */
  const cascadeVariantOptions: SelectOption[] = useMemo(() => {
    if (!cascadeCategory || !cascadeDrugName) return []
    return products
      .filter(
        (p) =>
          productMatchesCategory(p, cascadeCategory) &&
          p.display_name === cascadeDrugName
      )
      .map((p) => ({
        value: p.id,
        label: buildOptionLabel(p),
      }))
  }, [products, cascadeCategory, cascadeDrugName])

  // ─── Directions mode derived options (PORTAL-80) ─────────────────────────

  /** Distinct drugs (by display_name) for the directions-mode list, grouped
   *  under their primary category so the headers match the normal product
   *  dropdown. The specific product is chosen via the directions dropdown. */
  const drugOptions: ComboboxOptionGroup[] = useMemo(() => {
    if (!directionsMode || products.length === 0) return []
    const seen = new Set<string>()
    const grouped = new Map<string, { value: string; label: string }[]>()
    products.forEach((p) => {
      if (seen.has(p.display_name)) return
      seen.add(p.display_name)
      const primaryCategory = getProductCategories(p)[0]
      const option = { value: p.display_name, label: p.display_name }
      const existing = grouped.get(primaryCategory)
      if (existing) {
        existing.push(option)
      } else {
        grouped.set(primaryCategory, [option])
      }
    })
    return Array.from(grouped.keys())
      .sort(compareCategories)
      .map((category) => ({
        label: category,
        options: (grouped.get(category) || []).sort((a, b) =>
          a.label.localeCompare(b.label)
        ),
      }))
  }, [directionsMode, products])

  /** Direction options for the selected drug: one per sibling product,
   *  labeled by its dose-specific directions. Selecting one selects that
   *  product, whose quantity/days-supply/refills then auto-fill. */
  const directionOptions: SelectOption[] = useMemo(() => {
    if (!directionsMode || !selectedDrugName) return []
    return products
      .filter((p) => p.display_name === selectedDrugName)
      .map((p) => ({
        value: p.id,
        label: p.dose_specific_directions || buildOptionLabel(p),
      }))
  }, [directionsMode, selectedDrugName, products])

  // ─── Find product by ID ────────────────────────────────────────────────

  const findProductById = useCallback(
    (productId: string | null): CatalogProductForPortal | null => {
      if (!productId) return null
      return products.find((p) => p.id === productId) || null
    },
    [products]
  )

  // ─── Shared product selection ────────────────────────────────────────

  const selectProduct = useCallback(
    (productId: string | null, shouldValidate = true) => {
      setValue('product_id', productId || '', { shouldValidate })
      setProductSelected(!!productId)
      onProductSelect?.(productId ? findProductById(productId) : null)
    },
    [setValue, findProductById, onProductSelect]
  )

  // ─── Prefill effect (prescribe-again flow) ─────────────────────────────

  useEffect(() => {
    if (
      products.length > 0 &&
      currentProductId &&
      !prefillProcessed.current
    ) {
      const match = products.find((p) => p.id === currentProductId)
      if (match) {
        prefillProcessed.current = true
        setProductSelected(true)
        // Reflect the prefilled product in the directions dropdown.
        setSelectedDrugName(match.display_name)
        onProductSelect?.(match)
      }
    }
    // Only run when products load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products])

  // ─── Keep the directions drug picker in sync with the selected product ──
  // Portal config (hence directionsMode) loads async, so a product can be picked
  // via flat search before directions mode turns on. When it does, backfill the
  // drug from the selected product so the picker + Directions dropdown reflect it.
  useEffect(() => {
    if (!directionsMode || !currentProductId || selectedDrugName) return
    const match = findProductById(currentProductId)
    if (match) setSelectedDrugName(match.display_name)
  }, [directionsMode, currentProductId, selectedDrugName, findProductById])

  // ─── Flat search handler (resets cascade) ────────────────────────────

  const handleFlatSearchSelect = useCallback(
    (productId: string | null) => {
      setCascadeCategory(null)
      setCascadeDrugName(null)
      selectProduct(productId)
    },
    [selectProduct]
  )

  // ─── Cascade handlers ───────────────────────────────────────────────

  const handleCascadeCategoryChange = useCallback(
    (value: string) => {
      setCascadeCategory(value || null)
      setCascadeDrugName(null)
      // Clear product when browsing via cascade
      selectProduct(null)
    },
    [selectProduct]
  )

  const handleCascadeDrugChange = useCallback(
    (value: string) => {
      setCascadeDrugName(value || null)
      // Clear product — user still needs to pick variant
      selectProduct(null)
    },
    [selectProduct]
  )

  // Auto-select when only 1 variant for the chosen drug
  useEffect(() => {
    if (cascadeVariantOptions.length === 1 && currentProductId !== cascadeVariantOptions[0].value) {
      selectProduct(cascadeVariantOptions[0].value)
    }
  }, [cascadeVariantOptions, selectProduct, currentProductId])

  const handleCascadeVariantChange = useCallback(
    (value: string) => {
      selectProduct(value || null)
    },
    [selectProduct]
  )

  // ─── Recent product handler (resets cascade) ─────────────────────────

  const handleRecentSelect = useCallback(
    (product: CatalogProductForPortal) => {
      setCascadeCategory(null)
      setCascadeDrugName(null)
      // Keep the directions dropdown in sync when directions mode is on.
      setSelectedDrugName(product.display_name)
      selectProduct(product.id)
    },
    [selectProduct]
  )

  // ─── Directions mode handlers (PORTAL-80) ────────────────────────────────

  const handleDrugSelect = useCallback(
    (drugName: string | null) => {
      setSelectedDrugName(drugName)
      // Clear any selected product — the prescriber must pick a direction next,
      // so the form (and the directions dropdown) start blank. Skip validation
      // here so we don't surface "Please select a product" before they've had a
      // chance to choose a direction.
      selectProduct(null, false)
    },
    [selectProduct]
  )

  const handleDirectionSelect = useCallback(
    (productId: string) => {
      selectProduct(productId || null)
    },
    [selectProduct]
  )

  // In directions mode, once a drug is chosen the control that still needs a
  // value is the Directions dropdown — route the product_id error there rather
  // than to the drug picker.
  const directionsFieldError =
    directionsMode && !!selectedDrugName && !!errors.product_id

  // ─── Render ────────────────────────────────────────────────────────────

  // Loading state
  if (loading) {
    return (
      <div className={className}>
        <FormField label="Product" required htmlFor="product_id">
          <div className="flex items-center gap-2 py-3 px-4 text-[var(--color-gray-500)] border border-[var(--color-gray-300)] rounded-md bg-[var(--color-gray-50)]">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
            <span>Loading products...</span>
          </div>
        </FormField>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={className}>
        <FormField label="Product" required htmlFor="product_id" error={error}>
          <div className="py-3 px-4 text-[var(--color-error)] border border-[var(--color-error)] rounded-md bg-[var(--color-error-light)]">
            {error}
          </div>
        </FormField>
      </div>
    )
  }

  // Empty state
  if (products.length === 0) {
    return (
      <div className={className}>
        <FormField label="Product" required htmlFor="product_id">
          <div className="py-3 px-4 text-[var(--color-gray-500)] border border-[var(--color-gray-300)] rounded-md">
            No products available in the catalog.
          </div>
        </FormField>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* Recent products (hidden after product selected) */}
      <RecentProducts onSelect={handleRecentSelect} hidden={productSelected} />

      {/* ── Method 1: Flat search (full width, all variants) ── */}
      <FormField
        label="Product"
        required
        error={directionsFieldError ? undefined : errors.product_id?.message}
        htmlFor="product_id"
      >
        <input
          type="hidden"
          {...register('product_id', {
            required: 'Please select a product',
          })}
        />
        {directionsMode ? (
          /* Directions mode (PORTAL-80): list distinct drugs; the specific
             product is chosen via the directions dropdown below. */
          <Combobox
            options={drugOptions}
            value={selectedDrugName}
            onChange={handleDrugSelect}
            placeholder="Search products..."
            disabled={disabled}
            error={!!errors.product_id && !selectedDrugName}
            clearable
          />
        ) : (
          <Combobox
            options={groupedOptions}
            value={currentProductId || null}
            onChange={handleFlatSearchSelect}
            placeholder="Search products..."
            disabled={disabled}
            error={!!errors.product_id}
            clearable
          />
        )}
      </FormField>

      {/* ── Directions dropdown (PORTAL-80): blank until a direction is picked.
             Selecting one selects the underlying product and auto-fills its
             quantity/days-supply/refills. ── */}
      {directionsMode && selectedDrugName && (
        <div className="mt-4">
          <FormField
            label="Directions"
            required
            htmlFor="directions-select"
            error={directionsFieldError ? errors.product_id?.message : undefined}
          >
            <Select
              options={directionOptions}
              value={currentProductId || null}
              onChange={handleDirectionSelect}
              placeholder="Select directions..."
              disabled={disabled}
              error={directionsFieldError}
            />
          </FormField>
        </div>
      )}

      {/* ── OR divider + Method 2: Cascading browse ──
           Hidden in directions mode and when hide_category_browse is set. */}
      {!directionsMode && !hideCategoryBrowse && showCascade && (
        <>
          {/* Divider */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[var(--color-gray-300)]" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-3 text-sm text-[var(--color-gray-500)]">
                or browse by category
              </span>
            </div>
          </div>

          {/* Cascade: Category (full width) → Drug + Variant (50/50) */}
          <div className="space-y-4">
            <FormField label="Category" htmlFor="cascade-category">
              <Select
                options={cascadeCategoryOptions}
                value={cascadeCategory}
                onChange={handleCascadeCategoryChange}
                placeholder="Select category..."
                disabled={disabled}
              />
            </FormField>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Drug Name" htmlFor="cascade-drug">
                <Select
                  options={cascadeDrugOptions}
                  value={cascadeDrugName}
                  onChange={handleCascadeDrugChange}
                  placeholder="Select drug..."
                  disabled={disabled || !cascadeCategory}
                />
              </FormField>

              <FormField label="Strength / Size" htmlFor="cascade-variant">
                <Select
                  options={cascadeVariantOptions}
                  value={
                    cascadeCategory && cascadeDrugName
                      ? currentProductId || null
                      : null
                  }
                  onChange={handleCascadeVariantChange}
                  placeholder={
                    cascadeVariantOptions.length === 1
                      ? cascadeVariantOptions[0].label
                      : 'Select variant...'
                  }
                  disabled={
                    disabled ||
                    !cascadeDrugName ||
                    cascadeVariantOptions.length <= 1
                  }
                />
              </FormField>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ProductSelector
